/**
 * 確定（HIST §3.4・API §3.6）— 案件の変更を master へ入れ、行を `merged` に上書きする。
 *
 * 止める条件が 1 つ増える。**土台ずれ**である。案件の変更はノード全体の値で、確定はノードごと
 * 差し替えるので、案件を開いたあとに別の案件の確定で master が動くと、確定の瞬間に master 側の
 * 更新が黙って消える。行の `before`（書いたときの master）と今の master を**値で**比べ、違う
 * ノードが 1 つでもあれば止める。時刻ではなく値で比べるので、master が動いて元に戻った場合は
 * 引っ掛からない（HIST §3.4）。
 *
 * 結末は今と同じ 5 値である。
 *
 * | 値 | 意味 |
 * |---|---|
 * | `applied` | master へ入れ、案件の行を `merged` にした |
 * | `preview` | 試し実行。1 行も書いていないが、入る件数と止める理由は返す |
 * | `noop` | 変更が 0 行、または既に確定済みで、することが無い |
 * | `conflict` | 確定処理の最中に案件の変更の行が動いた。1 行も書いていない |
 * | `blocked` | 未承認（`pending` / `rejected`）が残るか、土台がずれている。1 行も書いていない |
 *
 * **確定処理の最中の衝突は、案件の変更の行の `updatedAt` の最大値で見る**（決定 7）。読んでから
 * 書くまでの間にその案件へ 1 行でも書かれたか、承認で状態が動けば最大値が進む。master 側の
 * 動きは土台ずれが値で捕まえるので、ここでは案件の側だけを見る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更を master へ入れ、土台ずれと未承認と最中の衝突で止める確定
 * @graph-connects composeElementSnapshot [calls] 案件を当てた姿の算出
 */
import { APPROVAL_REQUIRED_ELEMENT_TYPES, decodeElementValue, encodeElementValue, isPjMerged, } from './pj-changes.js';
/**
 * 確定の結末 5 値。今の `MergePjResult['outcome']` と同じ並びである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 確定の結末 5 値の allowlist
 * @graph-connects none
 */
export const ELEMENT_MERGE_OUTCOMES = [
    'applied',
    'preview',
    'noop',
    'conflict',
    'blocked',
];
/**
 * 確定を止める理由の種別 3 値。
 *
 * | 値 | 何が起きているか |
 * |---|---|
 * | `unapproved` | 承認が要る行に `pending` か `rejected` が残っている（今の制約 31） |
 * | `stale-base` | 行の `before` と今の master の値が違う（土台ずれ。HIST §3.4） |
 * | `concurrent-write` | 確定処理の最中に案件の変更の行が動いた |
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 確定を止める理由の種別 3 値の allowlist
 * @graph-connects none
 */
export const ELEMENT_MERGE_BLOCKER_KINDS = [
    'unapproved',
    'stale-base',
    'concurrent-write',
];
/**
 * その案件の行のうち、承認が済んでいないものを拾う。
 *
 * 承認が要らない型の行は `open` のままで確定できる。止めるのは `pending`（まだ判断していない）と
 * `rejected`（却下された）だけである（HIST §3.1）。
 * @graph-connects none
 */
function unapprovedNodeIds(rows) {
    return rows
        .filter((row) => row.status === 'pending' || row.status === 'rejected')
        .map((row) => row.nodeId);
}
/**
 * 土台がずれたノードを拾う。**時刻ではなく値で比べる。**
 *
 * 行の `before` はその案件がそのノードに最後に書いたときの master の値で、今の master の値と
 * 一致していれば土台は動いていない。比較は `encodeElementValue` の正規形（鍵を並べ替えた JSON）
 * で行うので、鍵の順の違いをずれと読まない。
 * @graph-connects none
 */
function staleBaseNodeIds(rows, master) {
    return rows
        .filter((row) => row.before !== encodeElementValue(master.get(row.nodeId) ?? null))
        .map((row) => row.nodeId);
}
/**
 * その案件の行から、master への差し替えを組む。
 *
 * `after` が null の行はノードを消す。それ以外は行の値でノードを入れ替える。案件が触っていない
 * ノードは 1 つも対象に入らない。
 * @graph-connects none
 */
function toMasterWrite(rows) {
    const upsert = new Map();
    const deleteNodeIds = [];
    for (const row of rows) {
        if (row.after === null) {
            deleteNodeIds.push(row.nodeId);
            continue;
        }
        upsert.set(row.nodeId, decodeElementValue(`案件 ${String(row.pjNumber)} のノード ${String(row.nodeId)}`, row.after));
    }
    return { upsert, deleteNodeIds };
}
/**
 * その案件の行が最後に動いた時刻。行が無ければ空文字。
 *
 * 確定処理の最中の衝突は、この値を確定の前後で比べて見る（決定 7）。書込も承認も打ち消しも
 * 行の `updatedAt` を進めるので、案件の側で起きたことはすべてこの 1 つの値に現れる。
 * @graph-connects none
 */
function latestUpdatedAt(rows) {
    return rows.reduce((latest, row) => (row.updatedAt > latest ? row.updatedAt : latest), '');
}
/**
 * 案件の変更を master へ確定する。
 *
 * `dryRun` のときは 1 行も書かず、入る予定のノードと止める理由だけを返す。
 *
 * @param args 確定する案件番号・確定した主体・試し実行の別
 * @param deps 置き場・master・採番・時刻
 * @returns 確定の結果
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 案件の変更を master へ入れ、案件の行を merged にし、確定を操作履歴へ足す
 * @graph-connects staleBaseNodeIds [calls] 土台ずれの検出
 * @graph-connects toMasterWrite [calls] master への差し替えの組み立て
 * @graph-connects unapprovedNodeIds [calls] その案件の行のうち、承認が済んでいないものを拾う
 * @graph-connects latestUpdatedAt [calls] その案件の行が最後に動いた時刻
 */
export async function mergeElementPj(args, deps) {
    const [rows, master] = await Promise.all([
        deps.store.listPjChanges(args.pjNumber),
        deps.master.listMaster(),
    ]);
    // ① 変更が 0 行、② 1 行でも merged。どちらも入れるものが無い。確定済みの案件へは書込も
    // 承認も通らないので、未反映の行が残ることはない。
    if (rows.length === 0 || isPjMerged(rows)) {
        return {
            pjNumber: args.pjNumber,
            outcome: 'noop',
            dryRun: args.dryRun,
            appliedNodeIds: [],
            blockers: [],
        };
    }
    const blockers = [];
    const unapproved = unapprovedNodeIds(rows);
    if (unapproved.length > 0) {
        blockers.push({
            kind: 'unapproved',
            nodeIds: unapproved,
            message: `承認が済んでいない変更が ${String(unapproved.length)} 件ある（${APPROVAL_REQUIRED_ELEMENT_TYPES.join(' / ')} は判断を経ないと確定できない）`,
        });
    }
    const stale = staleBaseNodeIds(rows, master);
    if (stale.length > 0) {
        blockers.push({
            kind: 'stale-base',
            nodeIds: stale,
            message: `土台がずれたノードが ${String(stale.length)} 件ある（その案件の番号で、今の master ＋ 案件の差分に書き直すと外れる）`,
        });
    }
    const appliedNodeIds = rows.map((row) => row.nodeId);
    if (args.dryRun) {
        return {
            pjNumber: args.pjNumber,
            outcome: 'preview',
            dryRun: true,
            appliedNodeIds,
            blockers,
        };
    }
    if (blockers.length > 0) {
        return {
            pjNumber: args.pjNumber,
            outcome: 'blocked',
            dryRun: false,
            appliedNodeIds: [],
            blockers,
        };
    }
    // ⑤ 読んでから書くまでの間に、この案件へ書込・承認・打ち消しが入っていないことを確かめる。
    // 入っていれば、その行は master へ入らないまま案件だけが確定してしまう。
    const before = latestUpdatedAt(rows);
    const latest = await deps.store.listPjChanges(args.pjNumber);
    if (latestUpdatedAt(latest) !== before || latest.length !== rows.length) {
        return {
            pjNumber: args.pjNumber,
            outcome: 'conflict',
            dryRun: false,
            appliedNodeIds: [],
            blockers: [
                {
                    kind: 'concurrent-write',
                    nodeIds: [],
                    message: '確定の最中に案件の変更の行が動いた。読み直してからやり直す',
                },
            ],
        };
    }
    const at = deps.now().toISOString();
    const opIds = await deps.allocateIds('operations', 1);
    // ⑥ master を差し替え、案件の行を merged にし、確定を履歴へ足す。master が先なのは、
    // 案件だけ merged になって master へ入っていない状態（永久に入らない）を作らないためである。
    await deps.master.applyMaster(toMasterWrite(rows));
    await deps.store.markPjChangesMerged(args.pjNumber);
    const operation = {
        opId: opIds.from,
        at,
        actor: args.actor,
        pjNumber: args.pjNumber,
        kind: 'merge',
        // 確定は案件そのものへの操作で、対象のノードを 1 つに決められない（HIST §3.2）。
        targetId: null,
        before: null,
        after: null,
        refOpId: null,
    };
    await deps.store.appendOperations([operation]);
    return {
        pjNumber: args.pjNumber,
        outcome: 'applied',
        dryRun: false,
        appliedNodeIds,
        blockers: [],
    };
}
//# sourceMappingURL=merge.js.map